import type { ValidationRule, ValidationError, KubernetesManifest } from '../types';
import { getValueByPath } from '../utils/yaml';
import { 
  smartEquals, 
  isNumeric, 
  toNumber, 
  isBoolean, 
  toBoolean,
  normalizeValue 
} from '../utils/yaml-normalize';

/**
 * Умный валидатор, который учитывает особенности студенческих YAML файлов
 */
export class SmartValidator {
  protected validateRule(
    manifest: KubernetesManifest,
    rule: ValidationRule
  ): ValidationError | null {
    const actualValue = getValueByPath(manifest, rule.path);

    switch (rule.operator) {
      case 'equals':
        // Используем умное сравнение вместо строгого
        if (!smartEquals(actualValue, rule.expectedValue)) {
          return {
            path: rule.path,
            expected: rule.expectedValue,
            actual: actualValue,
            message: this.enhanceErrorMessage(rule.description, actualValue, rule.expectedValue)
          };
        }
        break;

      case 'contains':
        if (!this.smartContains(actualValue, rule.expectedValue)) {
          return {
            path: rule.path,
            expected: rule.expectedValue,
            actual: actualValue,
            message: rule.description
          };
        }
        break;

      case 'exists':
        // Специальная логика для envFrom - проверяем и envFrom и env с секретами
        if (rule.path === 'spec.template.spec.containers[0].envFrom') {
          return this.validateSecretEnvironment(manifest, rule);
        }
        
        if (actualValue === undefined || actualValue === null) {
          return {
            path: rule.path,
            expected: 'существующее значение',
            actual: 'отсутствует',
            message: rule.description
          };
        }
        break;

      case 'notExists':
        if (actualValue !== undefined && actualValue !== null) {
          return {
            path: rule.path,
            expected: 'отсутствие значения',
            actual: actualValue,
            message: rule.description
          };
        }
        break;

      case 'greaterThan':
        const numericValue = this.getNumericValue(actualValue);
        if (numericValue === null || numericValue <= rule.expectedValue) {
          return {
            path: rule.path,
            expected: `больше ${rule.expectedValue}`,
            actual: actualValue,
            message: this.enhanceNumericErrorMessage(rule.description, actualValue)
          };
        }
        break;

      case 'lessThan':
        const numericValueLess = this.getNumericValue(actualValue);
        if (numericValueLess === null || numericValueLess >= rule.expectedValue) {
          return {
            path: rule.path,
            expected: `меньше ${rule.expectedValue}`,
            actual: actualValue,
            message: this.enhanceNumericErrorMessage(rule.description, actualValue)
          };
        }
        break;

      case 'in':
        if (!Array.isArray(rule.expectedValue)) {
          throw new Error('Для оператора "in" expectedValue должен быть массивом');
        }
        
        // Умная проверка включения с нормализацией
        const isInArray = rule.expectedValue.some(expected => smartEquals(actualValue, expected));
        if (!isInArray) {
          return {
            path: rule.path,
            expected: `одно из: ${JSON.stringify(rule.expectedValue)}`,
            actual: actualValue,
            message: this.enhanceInErrorMessage(rule.description, actualValue, rule.expectedValue)
          };
        }
        break;

      case 'notIn':
        if (!Array.isArray(rule.expectedValue)) {
          throw new Error('Для оператора "notIn" expectedValue должен быть массивом');
        }
        
        const isInArrayNot = rule.expectedValue.some(expected => smartEquals(actualValue, expected));
        if (isInArrayNot) {
          return {
            path: rule.path,
            expected: `не одно из: ${JSON.stringify(rule.expectedValue)}`,
            actual: actualValue,
            message: rule.description
          };
        }
        break;

      default:
        throw new Error(`Неподдерживаемый оператор: ${rule.operator}`);
    }

    return null;
  }

  private smartContains(container: any, value: any): boolean {
    if (Array.isArray(container)) {
      return container.some(item => smartEquals(item, value));
    }
    
    if (typeof container === 'object' && container !== null) {
      return smartEquals(container, value);
    }
    
    return smartEquals(container, value);
  }

  private getNumericValue(value: any): number | null {
    if (typeof value === 'number') return value;
    
    if (isNumeric(value)) {
      return toNumber(value);
    }
    
    return null;
  }

  private enhanceErrorMessage(baseMessage: string, actual: any, expected: any): string {
    let enhanced = baseMessage;
    
    // Если ожидается число, а пришла строка-число
    if (typeof expected === 'number' && typeof actual === 'string' && isNumeric(actual)) {
      enhanced += `\n💡 Совет: Возможно, вы указали число в кавычках. Попробуйте без кавычек: ${actual} вместо "${actual}"`;
    }
    
    // Если ожидается boolean, а пришла строка-boolean
    if (typeof expected === 'boolean' && typeof actual === 'string' && isBoolean(actual)) {
      const boolValue = toBoolean(actual);
      enhanced += `\n💡 Совет: Возможно, вы указали boolean в кавычках. Попробуйте без кавычек: ${boolValue} вместо "${actual}"`;
    }
    
    // Если есть лишние пробелы
    if (typeof actual === 'string' && actual !== actual.trim()) {
      enhanced += `\n💡 Совет: Проверьте пробелы в начале или конце значения`;
    }
    
    return enhanced;
  }

  private enhanceNumericErrorMessage(baseMessage: string, actual: any): string {
    let enhanced = baseMessage;
    
    if (typeof actual === 'string') {
      if (isNumeric(actual)) {
        enhanced += `\n💡 Совет: Вы указали число в кавычках ("${actual}"). Уберите кавычки: ${actual}`;
      } else {
        enhanced += `\n💡 Совет: Ожидается число, а получена строка: "${actual}"`;
      }
    } else if (actual === null || actual === undefined) {
      enhanced += `\n💡 Совет: Поле отсутствует. Добавьте числовое значение`;
    }
    
    return enhanced;
  }

  private enhanceInErrorMessage(baseMessage: string, actual: any, expectedValues: any[]): string {
    let enhanced = baseMessage;
    
    // Ищем похожие значения
    const normalizedActual = normalizeValue(actual);
    const similarValues = expectedValues.filter(expected => {
      const normalizedExpected = normalizeValue(expected);
      // Проверяем похожесть по типу или содержанию
      return typeof normalizedActual === typeof normalizedExpected;
    });
    
    if (similarValues.length > 0) {
      enhanced += `\n💡 Возможно, вы имели в виду: ${similarValues.join(', ')}`;
    }
    
    return enhanced;
  }

  public validate(manifests: KubernetesManifest[], rules: ValidationRule[]): ValidationError[] {
    const errors: ValidationError[] = [];

    for (const rule of rules) {
      let ruleMatched = false;
      let anyManifestMatched = false;
      
      for (const manifest of manifests) {
        const error = this.validateRule(manifest, rule);
        if (error) {
          // Для правил exists, мы проверяем все манифесты
          if (rule.operator === 'exists') {
            continue; // Продолжаем проверку других манифестов
          } else {
            errors.push(error);
            ruleMatched = true;
            break;
          }
        } else {
          // Правило прошло успешно для этого манифеста
          anyManifestMatched = true;
          if (rule.operator !== 'exists') {
            ruleMatched = true;
            break;
          }
        }
      }
      
      // Для правил exists, добавляем ошибку только если ни один манифест не прошел
      if (rule.operator === 'exists' && !anyManifestMatched) {
        errors.push({
          path: rule.path,
          expected: 'существующее значение',
          actual: 'отсутствует во всех манифестах',
          message: rule.description
        });
      }
    }

    return errors;
  }

  protected findManifestByKind(manifests: KubernetesManifest[], kind: string): KubernetesManifest | undefined {
    return manifests.find(manifest => manifest.kind === kind);
  }

  protected findAllManifestsByKind(manifests: KubernetesManifest[], kind: string): KubernetesManifest[] {
    return manifests.filter(manifest => manifest.kind === kind);
  }

  private validateSecretEnvironment(manifest: KubernetesManifest, rule: ValidationRule): ValidationError | null {
    if (manifest.kind !== 'Deployment') {
      // Для правил envFrom мы ищем только Deployment манифесты
      // Если это не Deployment, возвращаем ошибку "не найдено"
      return {
        path: rule.path,
        expected: 'Deployment манифест',
        actual: `${manifest.kind} манифест`,
        message: 'Данное правило применяется только к Deployment'
      };
    }

    const container = getValueByPath(manifest, 'spec.template.spec.containers[0]');
    if (!container) {
      return {
        path: 'spec.template.spec.containers[0]',
        expected: 'контейнер',
        actual: 'отсутствует',
        message: 'Deployment должен содержать минимум один контейнер'
      };
    }

    // Проверяем наличие envFrom с секретом db-credentials
    const envFrom = container.envFrom;
    const hasEnvFrom = Array.isArray(envFrom) && envFrom.some((env: any) => 
      env.secretRef?.name === 'db-credentials'
    );

    // Проверяем наличие env с valueFrom.secretKeyRef
    const env = container.env;
    const hasEnvWithSecret = Array.isArray(env) && env.some((envVar: any) => 
      envVar.valueFrom?.secretKeyRef?.name === 'db-credentials'
    );


    if (!hasEnvFrom && !hasEnvWithSecret) {
      return {
        path: rule.path,
        expected: 'envFrom с secretRef или env с valueFrom.secretKeyRef на db-credentials',
        actual: 'отсутствует',
        message: rule.description
      };
    }

    return null;
  }
}

export const smartValidator = new SmartValidator();
